import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearProviderError,
  getProviders,
} from '@/store/slices/provider.slice';
import { useDebounce } from '@/hooks/useDebounce';
import {
  ORGANISM_PROPS,
  ALL_PROVIDER,
  COMMON_DROPDOWN_CONSTANTS,
  DROPDOWN_OPTIONS,
  pagination as INITIAL_PAGINATION_STATE,
  INPUT_LENGTH_MIN_MAX_VALUES,
  LOCAL_STORAGE_KEYS,
} from '@/constant/global/global.constant';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import {
  VDI_TRANSACTION_LIMIT,
  GATEWAY_TRANSACTION_PAGE,
} from '@/pages/VdiTransaction/constant';
import {
  getAllMarketsListDetail,
  getMarketDetail,
  getMarketsListErrorDetails,
  getOperatorDetailWithPagination,
  getOperatorsListDetail,
} from '@/store/selectors/market.selector';
import {
  getLocationDetail,
  getLocationDetailsWithPagination,
  getLocationsListDetail,
  getlocationsListErrorDetails,
} from '@/store/selectors/locations.selector';
import {
  clearOperatorError,
  getOperatorsList,
} from '@/store/slices/operator.slice';
import {
  getProviderList,
  getProviderListErrorDetails,
  providerList,
} from '@/store/selectors/providers.selector';
import {
  clearLocationError,
  getLocationsList,
} from '@/store/slices/locations.slice';
import { clearMarketError, getMarketsList } from '@/store/slices/market.slice';
import {
  getDevicesListDetail,
  getdevicesListErrorDetails,
} from '@/store/selectors/device.selector';
import { clearDeviceError, getDevicesList } from '@/store/slices/device.slice';
import {
  getDataFromLocalStorage,
  setDataToLocalStorage,
} from '@/utils/retainFromLocalStorage';
import _ from 'lodash';
import { getOperatorListErrorDetails } from '@/store/selectors/operator.selector';
import Autocomplete from '../../elements/AutoComplete';

interface CommonDropdownSelectorProps {
  showProvider?: boolean;
  showOperator?: boolean;
  showLocation?: boolean;
  showMarket?: boolean;
  showDevice?: boolean;
  onSelectionChange: (selection: any) => void;
  id?: string;
  dataTestId?: string;
}

export const CommonDropdownSelector = ({
  showProvider = true,
  showOperator = true,
  showLocation = true,
  showMarket = false,
  showDevice = false,
  onSelectionChange,
  id,
  dataTestId,
}: CommonDropdownSelectorProps) => {
  const dispatch = useDispatch();

  // State variables
  const [selectedProvider, setSelectedProvider] = useState(ALL_PROVIDER);
  const [selectedOperator, setSelectedOperator] = useState(
    DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION,
  );
  const [selectedLocation, setSelectedLocation] = useState(
    DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
  );
  const [selectedMarket, setSelectedMarket] = useState(
    DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
  );

  const [providerName, onSearchByProviderName] = useState('');
  const [operatorName, onSearchByOperatorName] = useState('');
  const [locationName, onSearchByLocationName] = useState('');
  const [marketName, onSearchByMarketName] = useState('');
  const [deviceName, onSearchByDeviceName] = useState('');
  const [providerIdFromLocalStorage, setProviderIdFromLocalStorage] = useState<
    string | null
  >(null); // State for providerId
  const [operatorIdFromLocalStorage, setOperatorIdFromLocalStorage] = useState<
    string | null
  >(null); // State for operatorId

  const [dropdownSelectedValue, setSelectedDropdownValue] = useState({
    provider: ALL_PROVIDER,
    operator: DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION,
    location: DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
    device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
    market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
    selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.PROVIDERS,
  });
  const isAllProviderSelected = ALL_PROVIDER.id === selectedProvider?.id;
  const ALL_OPERATOR_SELECTED =
    dropdownSelectedValue?.operator?.id ===
    DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION.id;

  // Debounced search queries
  const searchProviderQuery = useDebounce(
    providerName?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const operatorQuery = useDebounce(
    operatorName,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const locationQuery = useDebounce(
    locationName,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const marketQuery = useDebounce(
    marketName,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const deviceQuery = useDebounce(
    deviceName,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  // Selectors
  const providersList = useSelector(providerList);
  const operatorList = useSelector(getOperatorsListDetail);
  const locationList = useSelector(getLocationDetail);
  const allMarketsList = useSelector(getAllMarketsListDetail);
  // Error Handling Selectors
  const providerDropdownErrorData = useSelector(getProviderListErrorDetails);
  const operatorDropdownErrorData = useSelector(getOperatorListErrorDetails);
  const marketDropdownErrorData = useSelector(getMarketsListErrorDetails);
  const locationsDropdownErrorData = useSelector(getlocationsListErrorDetails);
  const devicesDropdownErrorData = useSelector(getdevicesListErrorDetails);

  const { allDevicesListData, pagination: devicePaginationResponse } =
    useSelector(getDevicesListDetail);
  const { allLocationsListData, pagination: locationPaginationResponse } =
    useSelector(getLocationsListDetail);
  const {
    marketsList: { pagination: marketPaginationResponse },
  } = useSelector(getMarketDetail);
  const [isProviderIdLoaded, setIsProviderIdLoaded] = useState(false);
  const [isOperatorIdLoaded, setIsOperatorIdLoaded] = useState(false);

  const {
    operatorsList: { pagination: operatorPaginationResponse },
  } = useSelector(getOperatorDetailWithPagination);

  // Pagination state
  const [operatorPagination, setOperatorPagination] = useState(
    INITIAL_PAGINATION_STATE,
  );
  const [locationPagination, setLocationPagination] = useState(
    INITIAL_PAGINATION_STATE,
  );
  const [marketPagination, setMarketPagination] = useState(
    INITIAL_PAGINATION_STATE,
  );
  const [devicePagination, setDevicePagination] = useState(
    INITIAL_PAGINATION_STATE,
  );

  const fetchOperatorsList = useCallback(() => {
    dispatch(
      getOperatorsList({
        ...operatorPagination,
        ...(operatorQuery?.trim()?.length >= 3
          ? { keywords: operatorQuery?.trim() }
          : {}),
        providerId: selectedProvider?.id,
      }),
    );
  }, [dispatch, operatorPagination, operatorQuery, selectedProvider?.id]);

  const fetchLocationsList = useCallback(() => {
    const { selectedDropdownName } = dropdownSelectedValue;
    const allLocationsListParams: any = {
      ...(locationQuery?.trim().length >= 3
        ? { name: locationQuery?.trim() }
        : {}),
      ...locationPagination,
      ...(selectedDropdownName ===
        COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS && selectedOperator?.id
        ? { operatorId: selectedOperator.id }
        : {}),
    };
    if (!isOperatorIdLoaded) {
      return; // Prevent call if providerId is not yet processed
    }
    dispatch(getLocationsList({ ...allLocationsListParams }));
  }, [
    locationPagination,
    selectedOperator?.id,
    locationQuery,
    isOperatorIdLoaded,
  ]);

  const fetchDevicesList = async () => {
    const { selectedDropdownName } = dropdownSelectedValue;
    const allDevicesListParams = {
      ...devicePagination,
      includeInactive: true,
      ...(deviceQuery?.trim().length >= 3 ? { name: deviceQuery?.trim() } : {}),
      ...(selectedDropdownName ===
        COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS && selectedOperator?.id
        ? { operatorId: selectedOperator.id }
        : {}),
      ...(selectedDropdownName ===
        COMMON_DROPDOWN_CONSTANTS.SELECTORS.MARKETS && selectedMarket?.id
        ? { marketId: selectedMarket.id }
        : {}),
    };
    if (!isProviderIdLoaded || !isOperatorIdLoaded) {
      return; // Prevent call if providerId is not yet processed
    }
    dispatch(getDevicesList({ ...allDevicesListParams }));
  };

  const isNumeric = (inputValue: string) => {
    if (typeof inputValue !== 'string' || inputValue?.trim()?.length === 0) {
      return false;
    }
    const firstChar = inputValue?.trim()?.charAt(0);
    return !isNaN(parseInt(firstChar, 10));
  };

  const fetchMarketsList = async () => {
    const { selectedDropdownName } = dropdownSelectedValue;
    const allMarketsListParams = {
      ...(selectedDropdownName ===
        COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS && selectedOperator?.id
        ? { operatorId: selectedOperator.id }
        : {}),
      ...(selectedDropdownName ===
        COMMON_DROPDOWN_CONSTANTS.SELECTORS.PROVIDERS && selectedProvider?.id
        ? { providerId: selectedProvider.id }
        : {}),
      ...(marketQuery?.trim().length >= 1 && isNumeric(marketQuery)
        ? { sourceMarketId: marketQuery.trim() }
        : {}),
      ...(marketQuery?.trim().length >= 3 && !isNumeric(marketQuery)
        ? { name: marketQuery.trim() }
        : {}),
      includeInactive: true,
      ...marketPagination,
    };
    if (!isProviderIdLoaded || !isOperatorIdLoaded) {
      return; // Prevent call if providerId or OperatorId is not yet processed
    }
    dispatch(getMarketsList({ ...allMarketsListParams }));
  };

  const totalLocationPages = Math.ceil(
    locationPaginationResponse?.totalRecords / locationPagination.limit,
  );

  const totalOperatorPages = Math.ceil(
    operatorPaginationResponse?.totalRecords / operatorPagination.limit,
  );

  const totalMarketPages = Math.ceil(
    marketPaginationResponse?.totalRecords / marketPagination.limit,
  );

  const totalDevicePages = Math.ceil(
    devicePaginationResponse?.totalRecords / devicePagination?.limit,
  );

  useEffect(() => {
    const storedOperator = getDataFromLocalStorage(
      LOCAL_STORAGE_KEYS.OPERATOR_IN_LOCAL_STORAGE,
    );

    if (storedOperator) {
      const isSameOperator = storedOperator?.id === selectedOperator?.id;
      if (isSameOperator && showLocation) fetchLocationsList();
    } else if (selectedOperator && showLocation) fetchLocationsList();
    setSelectedLocation(DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION);
  }, [
    selectedOperator?.id,
    showOperator,
    fetchLocationsList,
    operatorIdFromLocalStorage,
    isOperatorIdLoaded,
  ]);

  useEffect(() => {
    if (showProvider) {
      dispatch(
        getProviders({
          page: GATEWAY_TRANSACTION_PAGE,
          limit: VDI_TRANSACTION_LIMIT,
          ...(searchProviderQuery?.trim()?.length >= 3
            ? { keywords: searchProviderQuery?.trim() }
            : {}),
        }),
      );
    }
  }, [dispatch, searchProviderQuery]);

  useEffect(() => {
    if (!isProviderIdLoaded) {
      return; // Prevent call if providerId is not yet processed
    }
    showOperator && fetchOperatorsList();
  }, [
    selectedProvider?.id,
    operatorQuery,
    isProviderIdLoaded,
    providerIdFromLocalStorage,
  ]);

  useEffect(() => {
    if (showMarket) {
      fetchMarketsList();
    }
  }, [
    selectedOperator?.id,
    operatorIdFromLocalStorage,
    isOperatorIdLoaded,
    marketQuery,
  ]);

  useEffect(() => {
    if (showDevice) {
      fetchDevicesList();
    }
  }, [
    selectedOperator?.id,
    selectedMarket?.id,
    dropdownSelectedValue?.market?.id,
    operatorIdFromLocalStorage,
    isOperatorIdLoaded,
    deviceQuery,
  ]);

  // Handle fetch more data
  const handleFetchMore = (type: string) => {
    let paginationState;
    let totalPages;
    let fetchFunction;
    let updatePagination;
    let updateData;
    let dataList;
    let idKey;
    let selectorType: string | undefined;
    let condition; // Added condition variable

    switch (type) {
      case COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS:
        paginationState = operatorPagination;
        totalPages = totalOperatorPages;
        fetchFunction = fetchOperatorsList;
        updatePagination = setOperatorPagination;
        dataList = operatorList;
        idKey = COMMON_DROPDOWN_CONSTANTS.FETCH_MORE_TYPE.OPERATORS_KEY;
        condition = paginationState?.page <= totalPages; // Less than for operators
        break;
      case COMMON_DROPDOWN_CONSTANTS.SELECTORS.LOCATIONS:
        paginationState = locationPagination;
        totalPages = totalLocationPages;
        fetchFunction = fetchLocationsList;
        updatePagination = setLocationPagination;
        dataList = allLocationsListData;
        idKey = COMMON_DROPDOWN_CONSTANTS.FETCH_MORE_TYPE.OPERATORS_KEY;
        condition = paginationState?.page <= totalPages; // Less than for locations
        break;
      case COMMON_DROPDOWN_CONSTANTS.SELECTORS.MARKETS:
        paginationState = marketPagination;
        totalPages = totalMarketPages;
        fetchFunction = fetchMarketsList;
        updatePagination = setMarketPagination;
        dataList = allMarketsList;
        idKey = COMMON_DROPDOWN_CONSTANTS.FETCH_MORE_TYPE.MARKETS_ID_KEY;
        selectorType = COMMON_DROPDOWN_CONSTANTS.SELECTORS.MARKETS;
        condition = paginationState?.page <= totalPages; // Less than or equal for markets
        break;
      case COMMON_DROPDOWN_CONSTANTS.SELECTORS.DEVICES:
        paginationState = devicePagination;
        totalPages = totalDevicePages;
        fetchFunction = fetchDevicesList;
        updatePagination = setDevicePagination;
        dataList = allDevicesListData;
        idKey = COMMON_DROPDOWN_CONSTANTS.FETCH_MORE_TYPE.DEVICES_ID_KEY;
        selectorType = COMMON_DROPDOWN_CONSTANTS.SELECTORS.DEVICES;
        condition = paginationState?.page <= totalPages; // Less than or equal for devices
        break;
      default:
        return;
    }

    if (condition) {
      // Use the condition variable
      updatePagination((prev) => ({ ...prev, page: prev?.page + 1 }));
      paginationState?.page > 1 && fetchFunction();
    }
  };

  const handleLocalProvider = (provider: any) => {
    setDataToLocalStorage(
      LOCAL_STORAGE_KEYS.PROVIDER_IN_LOCAL_STORAGE,
      provider,
    );
    handleLocalOperator(null);
  };
  const handleLocalOperator = (operator: any) => {
    setDataToLocalStorage(
      LOCAL_STORAGE_KEYS.OPERATOR_IN_LOCAL_STORAGE,
      operator,
    );
  };

  useEffect(() => {
    const provider = getDataFromLocalStorage(
      LOCAL_STORAGE_KEYS.PROVIDER_IN_LOCAL_STORAGE,
    );
    const providerData = provider == null ? ALL_PROVIDER : provider; // Use default if not found in localStorage
    setIsProviderIdLoaded(true);
    setProviderIdFromLocalStorage(providerData?.id);
    const operator = getDataFromLocalStorage(
      LOCAL_STORAGE_KEYS.OPERATOR_IN_LOCAL_STORAGE,
    );
    const operatorData =
      operator == null
        ? DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION
        : operator;
    setIsOperatorIdLoaded(true);
    setOperatorIdFromLocalStorage(operatorData?.id);
    if (showProvider) {
      handleSelectedLocal(providerData, operatorData);
    } else {
      handleSelectedLocal(ALL_PROVIDER, operatorData);
    }
  }, []);

  const dropdownDisplayValueForMarkets = (marketRecord: any) => {
    const truncatedMarketName =
      marketRecord?.name?.length > 15
        ? marketRecord?.name?.slice(0, 15)
        : marketRecord?.name;
    const truncatedSourceMarketId =
      marketRecord?.sourceId?.length > 30
        ? marketRecord?.sourceId?.slice(0, 30)
        : marketRecord?.sourceId;
    const dropdownDisplayValue = `${truncatedSourceMarketId} - ${truncatedMarketName}`;
    return marketRecord?.name ===
      DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION.name
      ? DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION.name
      : dropdownDisplayValue;
  };

  const handleSelectedLocal = (localProvider: any, localOperator: any) => {
    setSelectedProvider(localProvider);
    setSelectedOperator(localOperator);
    setSelectedDropdownValue({
      provider: localProvider,
      operator: localOperator,
      location: DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
      market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
      device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
      selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS,
    });
    onSelectionChange({
      provider: localProvider,
      operator: localOperator,
      location: DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
      market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
      device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
      selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS,
    });
  };
  const resetOtherDropdownsPaginations = (selector: string) => {
    selector === COMMON_DROPDOWN_CONSTANTS.SELECTORS.PROVIDERS &&
      showOperator &&
      setOperatorPagination(INITIAL_PAGINATION_STATE);
    showMarket && setMarketPagination(INITIAL_PAGINATION_STATE);
    showLocation && setLocationPagination(INITIAL_PAGINATION_STATE);
    showDevice && setDevicePagination(INITIAL_PAGINATION_STATE);
  };
  const handleSelectedProvider = (value: any) => {
    handleLocalProvider(value);
    setSelectedProvider(value);
    setSelectedOperator(DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION);
    setSelectedLocation(DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION);
    setSelectedMarket(DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION);
    onSearchByOperatorName('');
    onSearchByLocationName('');
    onSearchByMarketName('');
    onSearchByDeviceName('');
    resetOtherDropdownsPaginations(
      COMMON_DROPDOWN_CONSTANTS.SELECTORS.PROVIDERS,
    );
    setSelectedDropdownValue({
      provider: value,
      operator: DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION,
      location: DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
      market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
      device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
      selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.PROVIDERS,
    });
    onSelectionChange({
      provider: value,
      operator: DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION,
      location: DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
      market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
      device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
      selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.PROVIDERS,
    });
  };
  const handleSelectedOperator = (value: any) => {
    handleLocalOperator(value);
    setSelectedOperator(value);
    setSelectedLocation(DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION);
    setSelectedMarket(DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION);
    onSearchByLocationName('');
    onSearchByMarketName('');
    onSearchByDeviceName('');
    resetOtherDropdownsPaginations(
      COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS,
    );
    setSelectedDropdownValue({
      provider: selectedProvider,
      operator: value,
      location: DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
      market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
      device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
      selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS,
    });
    onSelectionChange({
      provider: selectedProvider,
      operator: value,
      location: DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
      market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
      device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
      selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS,
    });
  };

  // Error Messages Display in UI toast
  useEffect(() => {
    const errorSources = [
      providerDropdownErrorData,
      operatorDropdownErrorData,
      marketDropdownErrorData,
      locationsDropdownErrorData,
      devicesDropdownErrorData,
    ];
  }, [
    providerDropdownErrorData,
    operatorDropdownErrorData,
    marketDropdownErrorData,
    locationsDropdownErrorData,
    devicesDropdownErrorData,
  ]);

  // Error Messages Cleanup while unmounting this component
  useEffect(() => {
    return () => {
      dispatch(clearProviderError());
      dispatch(clearOperatorError());
      dispatch(clearMarketError());
      dispatch(clearLocationError());
      dispatch(clearDeviceError());
    };
  }, [dispatch]);

  // Selectors configuration
  const selectors = [
    {
      show: showProvider,
      label: ORGANISM_PROPS.GROUP_DROPDOWN.PROVIDER_LABEL,
      placeholder: ORGANISM_PROPS.GROUP_DROPDOWN.PROVIDER_PLACEHOLDER,
      selectedValue: dropdownSelectedValue?.provider,
      lists: providersList,
      handleSelectedValue: handleSelectedProvider,
      handleSearch: onSearchByProviderName,
      handleFetchMore: () =>
        handleFetchMore(COMMON_DROPDOWN_CONSTANTS.SELECTORS.PROVIDERS),
      disabled: false,
      nameKey: 'profile',
      dataTestId: dataTestId ? `${dataTestId}-provider` : undefined,
    },
    {
      show: showOperator,
      label: ORGANISM_PROPS.GROUP_DROPDOWN.OPERATOR_LABEL,
      placeholder: ORGANISM_PROPS.GROUP_DROPDOWN.OPERATOR_PLACEHOLDER,
      selectedValue: dropdownSelectedValue?.operator,
      lists: operatorList,
      handleSelectedValue: handleSelectedOperator,
      handleSearch: onSearchByOperatorName,
      handleFetchMore: () =>
        handleFetchMore(COMMON_DROPDOWN_CONSTANTS.SELECTORS.OPERATORS),
      disabled: !selectedProvider,
      dataTestId: dataTestId ? `${dataTestId}-operator` : undefined,
    },
    {
      show: showLocation,
      label: ORGANISM_PROPS.GROUP_DROPDOWN.LOCATION_LABEL,
      placeholder: ORGANISM_PROPS.GROUP_DROPDOWN.LOCATION_PLACEHOLDER,
      selectedValue: dropdownSelectedValue?.location,
      lists: allLocationsListData,
      handleSelectedValue: (value: any) => {
        setSelectedLocation(value);
        setSelectedMarket(DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION);
        onSearchByMarketName('');
        onSearchByDeviceName('');
        setSelectedDropdownValue({
          provider: dropdownSelectedValue?.provider,
          operator: dropdownSelectedValue?.operator,
          location: value,
          market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
          device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
          selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.LOCATIONS,
        });
        onSelectionChange({
          provider: dropdownSelectedValue?.provider,
          operator: dropdownSelectedValue?.operator,
          location: value,
          market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
          device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
          selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.LOCATIONS,
        });
        resetOtherDropdownsPaginations(
          COMMON_DROPDOWN_CONSTANTS.SELECTORS.LOCATIONS,
        );
      },
      handleSearch: onSearchByLocationName,
      handleFetchMore: () =>
        handleFetchMore(COMMON_DROPDOWN_CONSTANTS.SELECTORS.LOCATIONS),
      disabled: !selectedOperator || allLocationsListData?.length === 0,
      dataTestId: dataTestId ? `${dataTestId}-location` : undefined,
    },
    {
      show: showMarket,
      label: ORGANISM_PROPS.GROUP_DROPDOWN.MARKET_LABEL,
      placeholder: ORGANISM_PROPS.GROUP_DROPDOWN.MARKET_PLACEHOLDER,
      selectedValue: dropdownSelectedValue?.market,
      lists: allMarketsList?.map((item) => ({
        ...item,
        id: item?.id,
        marketsDropdownDisplayValue: dropdownDisplayValueForMarkets(item),
      })),
      nameKey: 'marketsDropdownDisplayValue',
      handleSelectedValue: (value: any) => {
        setSelectedMarket(value);
        onSearchByDeviceName('');
        setSelectedDropdownValue({
          provider: selectedProvider,
          operator: selectedOperator,
          location: selectedLocation,
          market: value,
          device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
          selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.MARKETS,
        });
        onSelectionChange({
          provider: dropdownSelectedValue?.provider,
          operator: dropdownSelectedValue?.operator,
          location: dropdownSelectedValue?.location,
          market: value,
          device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
          selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.MARKETS,
        });
        resetOtherDropdownsPaginations(
          COMMON_DROPDOWN_CONSTANTS.SELECTORS.MARKETS,
        );
      },
      handleSearch: onSearchByMarketName,
      handleFetchMore: () =>
        handleFetchMore(COMMON_DROPDOWN_CONSTANTS.SELECTORS.MARKETS),
      disabled: allMarketsList?.length === 0,
      dataTestId: dataTestId ? `${dataTestId}-market` : undefined,
    },
    {
      show: showDevice,
      label: ORGANISM_PROPS.GROUP_DROPDOWN.DEVICE_LABEL,
      placeholder: ORGANISM_PROPS.GROUP_DROPDOWN.DEVICE_PLACEHOLDER,
      selectedValue: dropdownSelectedValue?.device,
      lists: allDevicesListData,
      handleSelectedValue: (value: any) => {
        setSelectedDropdownValue({
          provider: selectedProvider,
          operator: selectedOperator,
          location: selectedLocation,
          market: selectedMarket,
          device: value,
          selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.DEVICES,
        });
        onSelectionChange({
          provider: dropdownSelectedValue?.provider,
          operator: dropdownSelectedValue?.operator,
          location: dropdownSelectedValue?.location,
          market: dropdownSelectedValue?.market,
          device: value,
          selectedDropdownName: COMMON_DROPDOWN_CONSTANTS.SELECTORS.DEVICES,
        });
        resetOtherDropdownsPaginations(
          COMMON_DROPDOWN_CONSTANTS.SELECTORS.DEVICES,
        );
      },
      nameKey: 'sourceId',
      handleSearch: onSearchByDeviceName,
      handleFetchMore: () =>
        handleFetchMore(COMMON_DROPDOWN_CONSTANTS.SELECTORS.DEVICES),
      disabled: allDevicesListData?.length === 0,
      dataTestId: dataTestId ? `${dataTestId}-device` : undefined,
    },
  ];

  return (
    <div className="flex flex-row" id={id} data-testid={dataTestId ?? ""}>
      {selectors.map(
        (selector, index) =>
          selector.show && (
            <Autocomplete
              key={index}
              placeholder={selector.placeholder}
              selectedValue={selector.selectedValue}
              lists={selector.lists}
              label={selector.label}
              handleSelectedValue={selector.handleSelectedValue}
              handleSearch={selector.handleSearch}
              handleFetchMore={selector?.handleFetchMore}
              disabled={selector?.disabled}
              nameKey={selector?.nameKey}
              dataTestId={selector?.dataTestId}
            />
          ),
      )}
    </div>
  );
};

export default CommonDropdownSelector;
